import { useState, useEffect, useCallback } from 'react';
import { useAccessToken } from './useAuth';
import auditLogService from '../services/auditLogService';

/**
 * Custom hook for fetching and managing audit logs
 * @param {Object} options - Hook options
 * @param {string} options.type - Type of query (recent, user, action, resource, today, timeRange)
 * @param {Object} options.params - Additional parameters based on type
 * @param {number} options.page - Page number (default: 0)
 * @param {number} options.size - Page size (default: 50)
 * @param {boolean} options.autoFetch - Whether to fetch automatically on mount (default: true)
 * @returns {Object} Audit log data and methods
 */
export const useAuditLogs = ({ 
    type = 'recent', 
    params = {}, 
    page = 0, 
    size = 50,
    autoFetch = true 
} = {}) => {
    const { getBackendAccessToken } = useAccessToken();
    const [auditLogs, setAuditLogs] = useState([]);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [currentPage, setCurrentPage] = useState(page);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchAuditLogs = useCallback(async (pageNum = currentPage) => {
        setLoading(true);
        setError(null);
        
        try {
            const token = await getBackendAccessToken();
            let response;

            switch (type) {
                case 'recent':
                    response = await auditLogService.getRecent(token, pageNum, size);
                    break;
                
                case 'user':
                    if (!params.userOid) {
                        throw new Error('userOid is required for user audit logs');
                    }
                    response = await auditLogService.getByUser(token, params.userOid, pageNum, size);
                    break;
                
                case 'action':
                    if (!params.action) {
                        throw new Error('action is required for action audit logs');
                    }
                    response = await auditLogService.getByAction(token, params.action, pageNum, size);
                    break;
                
                case 'resource':
                    if (!params.resourceType || !params.resourceId) {
                        throw new Error('resourceType and resourceId are required for resource audit logs');
                    }
                    response = await auditLogService.getByResource(
                        token, 
                        params.resourceType, 
                        params.resourceId, 
                        pageNum, 
                        size
                    );
                    break;
                
                case 'today':
                    response = await auditLogService.getToday(token, pageNum, size);
                    break;
                
                case 'timeRange':
                    if (!params.start || !params.end) {
                        throw new Error('start and end dates are required for time range audit logs');
                    }
                    response = await auditLogService.getByTimeRange(
                        token, 
                        params.start, 
                        params.end, 
                        pageNum, 
                        size
                    );
                    break;
                
                case 'correlation':
                    if (!params.corrId) {
                        throw new Error('corrId is required for correlation audit logs');
                    }
                    response = await auditLogService.getByCorrelationId(token, params.corrId);
                    // Correlation endpoint doesn't return paginated data
                    setAuditLogs(response || []);
                    setTotalElements(response?.length || 0);
                    setTotalPages(1);
                    setLoading(false);
                    return;
                
                default:
                    throw new Error(`Unknown audit log type: ${type}`);
            }

            setAuditLogs(response.content || []);
            setTotalElements(response.totalElements || 0);
            setTotalPages(response.totalPages || 0);
            setCurrentPage(pageNum);
        } catch (err) {
            console.error('Error fetching audit logs:', err);
            setError(err.message || 'Failed to fetch audit logs');
            setAuditLogs([]);
        } finally {
            setLoading(false);
        }
    }, [type, params, size, currentPage, getBackendAccessToken]);

    const refresh = useCallback(() => {
        fetchAuditLogs(currentPage);
    }, [fetchAuditLogs, currentPage]);

    const nextPage = useCallback(() => {
        if (currentPage < totalPages - 1) {
            fetchAuditLogs(currentPage + 1);
        }
    }, [currentPage, totalPages, fetchAuditLogs]);

    const previousPage = useCallback(() => {
        if (currentPage > 0) {
            fetchAuditLogs(currentPage - 1);
        }
    }, [currentPage, fetchAuditLogs]);

    const goToPage = useCallback((pageNum) => {
        if (pageNum >= 0 && pageNum < totalPages) {
            fetchAuditLogs(pageNum);
        }
    }, [totalPages, fetchAuditLogs]);

    useEffect(() => {
        if (autoFetch) {
            fetchAuditLogs(page);
        }
    }, [autoFetch]); // Only run on mount if autoFetch is true

    return {
        auditLogs,
        totalElements,
        totalPages,
        currentPage,
        loading,
        error,
        refresh,
        nextPage,
        previousPage,
        goToPage,
        hasNextPage: currentPage < totalPages - 1,
        hasPreviousPage: currentPage > 0
    };
};

/**
 * Custom hook for fetching user's recent activity
 * @param {string} userOid - Azure AD user object ID
 * @param {number} limit - Number of recent items to fetch (default: 10)
 * @returns {Object} Recent activity data and methods
 */
export const useUserRecentActivity = (userOid, limit = 10) => {
    const { getBackendAccessToken } = useAccessToken();
    const [recentActivity, setRecentActivity] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchRecentActivity = useCallback(async () => {
        if (!userOid) {
            setRecentActivity([]);
            return;
        }

        setLoading(true);
        setError(null);
        
        try {
            const token = await getBackendAccessToken();
            const response = await auditLogService.getByUser(token, userOid, 0, limit);
            setRecentActivity(response.content || []);
        } catch (err) {
            console.error('Error fetching recent activity:', err);
            setError(err.message || 'Failed to fetch recent activity');
            setRecentActivity([]);
        } finally {
            setLoading(false);
        }
    }, [userOid, limit, getBackendAccessToken]);

    useEffect(() => {
        fetchRecentActivity();
    }, [fetchRecentActivity]);

    return {
        recentActivity,
        loading,
        error,
        refresh: fetchRecentActivity
    };
};

/**
 * Custom hook for fetching resource history
 * @param {string} resourceType - Type of resource (Document, Client, etc.)
 * @param {string} resourceId - ID of the resource
 * @param {number} limit - Number of history items to fetch (default: 50)
 * @returns {Object} Resource history data and methods
 */
export const useResourceHistory = (resourceType, resourceId, limit = 50) => {
    const { getBackendAccessToken } = useAccessToken();
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchHistory = useCallback(async () => {
        if (!resourceType || !resourceId) {
            setHistory([]);
            return;
        }

        setLoading(true);
        setError(null);
        
        try {
            const token = await getBackendAccessToken();
            const response = await auditLogService.getByResource(
                token, 
                resourceType, 
                resourceId, 
                0, 
                limit
            );
            setHistory(response.content || []);
        } catch (err) {
            console.error('Error fetching resource history:', err);
            setError(err.message || 'Failed to fetch resource history');
            setHistory([]);
        } finally {
            setLoading(false);
        }
    }, [resourceType, resourceId, limit, getBackendAccessToken]);

    useEffect(() => {
        fetchHistory();
    }, [fetchHistory]);

    return {
        history,
        loading,
        error,
        refresh: fetchHistory
    };
};

export default useAuditLogs;
